import { WorkflowCanvas } from "./workflowBuilderData";
import { WorkflowTerminalStatus } from "./WorkflowTerminalStage";
import { WorkflowPipelineStepState } from "./WorkflowRunRail";
import { WorkflowRunCase } from "../../hooks/useWorkflowRunInstance";
import { WorkflowInstanceValueInputDto } from "../../types/workflowDto";

/** Header pill per stage — the prototype labels what the stage is *for*, not its type. Only the
 *  flagship PIF stages have bespoke runtime screens, so only they need their own pill. */
export const PIF_STAGE_PILLS: Record<string, string> = {
  "pif-1": "Identity",
  "pif-2": "Routing",
  "pif-3": "PIF",
};

/** Every field of the stage, typed or not — the server re-checks required on submit. */
export const toStageValueInputs = (
  stage: WorkflowCanvas,
  values: Record<string, string>,
): WorkflowInstanceValueInputDto[] =>
  stage.fields.map((field) => ({ fieldId: Number(field.id), value: values[field.id] ?? "" }));

/** "2026-10-06T14:22:05" -> "2026-10-06 14:22". Portal timestamps are already Eastern, so they
 *  are printed as stored — never re-converted through a time-zone-aware formatter. */
export const toStampTime = (value: string | null | undefined): string =>
  value ? value.replace("T", " ").slice(0, 16) : "";

/** The engine's VarKey limit leaves room for ".httpStatus" (sProc_System_WorkflowWebservice_Capture_IU). */
const WEBSERVICE_STAGE_NAME_LIMIT = 69;

/**
 * Whether a web-service stage the case has already left FAILED (its call ran and onFailure let the
 * case continue). The engine writes `{stage}.success` = 1 / 0 after every call. It names that
 * variable after the stage's NAME today (truncated), not its stage key, despite decision 1.1, so
 * both are read — the key first, for when the API is corrected.
 */
export const webServiceStageFailed = (runCase: WorkflowRunCase, stage: WorkflowCanvas | undefined): boolean => {
  if (!stage || !runCase.doneStageIds.includes(stage.id)) return false;
  const names = [stage.stageKey, stage.name.slice(0, WEBSERVICE_STAGE_NAME_LIMIT)].filter(Boolean);
  const success = names.map((name) => runCase.variables[`${name}.success`]).find((value) => value !== undefined);
  return success === "0" || success?.toLowerCase() === "false";
};

export const isOpenCase = (runCase: WorkflowRunCase) =>
  runCase.status === "draft" || runCase.status === "active";

/** The engine has only completed / cancelled as end states; a rejection is read from the
 *  approvals, since a reject gate can route the case anywhere the definition says. */
export const toTerminalStatus = (runCase: WorkflowRunCase): WorkflowTerminalStatus | null => {
  if (isOpenCase(runCase)) return null;
  if (runCase.rejectedStageId) return "rejected";
  return runCase.status === "cancelled" ? "cancelled" : "completed";
};

export const toStatusLabel = (terminalStatus: WorkflowTerminalStatus | null, notStarted: boolean): string => {
  if (notStarted) return "Not started";
  if (!terminalStatus) return "In progress";
  if (terminalStatus === "completed") return "Completed";
  return terminalStatus === "rejected" ? "Rejected" : "Cancelled";
};

/**
 * The rail's step states and "X/N" count. A real case ticks the stages its timeline says it has
 * left — a conditional can skip stages, so "everything before the current one" would be wrong.
 * With no case yet (a PIF on its HR gate) the first stage is simply current.
 */
export const toPipeline = (
  stages: WorkflowCanvas[],
  stageIndex: number,
  terminalStatus: WorkflowTerminalStatus | null,
  runCase: WorkflowRunCase | null,
): { stepStates: WorkflowPipelineStepState[]; progressCount: number } => {
  const stepStates = stages.map((stage, index): WorkflowPipelineStepState => {
    if (terminalStatus === "completed") return "done";
    if (runCase) {
      if (terminalStatus === "rejected" && stage.id === runCase.rejectedStageId) return "rejected";
      if (!terminalStatus && index === stageIndex) return "current";
      return runCase.doneStageIds.includes(stage.id) ? "done" : "waiting";
    }
    if (index < stageIndex) return "done";
    if (!terminalStatus && index === stageIndex) return "current";
    if (terminalStatus === "rejected" && index === stageIndex) return "rejected";
    return "waiting";
  });
  const progressCount = runCase
    ? terminalStatus === "completed"
      ? stages.length
      : stepStates.filter((state) => state === "done").length + (terminalStatus ? 0 : 1)
    : stageIndex + 1;
  return { stepStates, progressCount };
};
